<template>
	<div class="container col-lg-10 px-4 py-5 mt-5">
		<div class="row flex-row-reverse align-items-center g-5">

			<!-- Right: login form -->
			<div class="col-12 col-md-5">
				<div class="mb-5">
					<img src="@/assets/equivate_logo.svg" alt="Equivate logo">
				</div>

				<div v-if="error" class="alert alert-danger" role="alert">
					{{ error }}
				</div>

				<div v-if="expired" class="alert alert-warning" role="alert">
					Your session has expired. Please sign in again.
				</div>

				<!-- While providers load (and during the redirect to the single provider) -->
				<div v-if="loading" class="text-center py-4">
					<div class="spinner-border text-primary" role="status">
						<span class="visually-hidden">Redirecting to sign-in…</span>
					</div>
				</div>

				<!-- Shown only when we can't auto-redirect (an error/expired prompt, or more than one provider). -->
				<div v-else>
					<p class="mb-4">Sign in to continue</p>

					<div v-if="providers.length === 0" class="alert alert-info">
						No authentication providers configured.
					</div>

					<div v-else class="d-grid gap-2 mb-3">
						<a
							v-for="provider in providers"
							:key="provider.name"
							:href="provider.loginUrl"
							class="sso-button"
						>
							<span class="sso-icon">
								<component :is="getProviderIcon(provider.iconName || provider.name)" />
							</span>
							{{ provider.displayName || provider.name }}
						</a>
					</div>
				</div>

				<div v-if="error" class="text-center mt-4">
					<a href="#" class="small text-muted" @click.prevent="switchAccount">
						<template v-if="loggedInAsEmail">Not {{ loggedInAsEmail }}? Sign out and try again</template>
						<template v-else>Not you? Sign out and try again</template>
					</a>
				</div>
			</div>

			<!-- Left: illustration -->
			<div class="col-12 col-md-7">
				<img src="@/assets/equivate_login.gif" class="login-image" alt="">
			</div>

		</div>
	</div>
</template>

<script>
import { authService } from '@/auth'
import { API_BASE_URL } from '@/auth/authService'
import { MicrosoftIcon, KeycloakIcon, DefaultIcon } from '@/components/icons/ProviderIcons'

const iconMap = {
	microsoft: MicrosoftIcon,
	key: KeycloakIcon
}

export default {
	name: 'LoginView',

	data() {
		return {
			providers: [],
			loading: true,
			error: null,
			expired: false,
			loggedInAsEmail: null,
			sid: null
		}
	},

	async mounted() {
		const errorCode = this.$route.query.error
		this.error = errorCode ? this.resolveErrorMessage(errorCode) : null
		this.expired = this.$route.query.expired === 'true'
		this.loggedInAsEmail = this.$route.query.email || null
		this.sid = this.$route.query.sid || null

		// The Keycloak/Entra session is left alone until the user notices the error and
		// clicks "sign out and try again" themselves — nothing auto-triggers on mount.

		try {
			this.providers = await authService.getProviders()
		} catch (e) {
			this.error = 'Unable to load authentication providers.'
			this.loading = false
			return
		}

		// Skip the provider-selection page: when there's exactly one provider and nothing
		// to show the user (no error / not an expired-session prompt), go straight to it.
		// The provider (Keycloak) presents its own login options from there.
		if (!this.error && !this.expired && this.providers.length === 1) {
			window.location.href = this.providers[0].loginUrl
			return // keep the spinner visible during the redirect
		}

		this.loading = false
	},

	methods: {
		resolveErrorMessage(code) {
			const messages = {
				not_invited: 'You have not been invited to Equivate. Please contact your administrator to request access.',
				provider_domain_mismatch: 'Use the sign-in provider assigned to your email domain.',
				token_processing_failed: 'Authentication failed due to a technical error. Please try again.',
				auth_failed: 'Authentication failed. Please try again.',
				no_credentials: 'No authentication provider is configured for your organization. Please contact your administrator.',
				invalid_state: 'Your login session expired. Please try again.',
			}
			return messages[code] || 'An unexpected error occurred during sign in. Please try again.'
		},

		getProviderIcon(name) {
			return iconMap[name] || DefaultIcon
		},

		// Ends the current Keycloak/IdP session so a cached session (e.g. from a wrong
		// provider, or an interrupted not_invited flow) doesn't silently re-auth as the
		// same account on the next attempt.
		async switchAccount() {
			// A hard reject (no Equivate user at all) never gets a JWT, so there's no
			// session for authService.logout() to act on — the id_token it needs was
			// instead stashed server-side at rejection time, referenced by this id.
			if (this.sid) {
				window.location.href = `${API_BASE_URL}/api/oauth/end-rejected-session?sid=${encodeURIComponent(this.sid)}`
				return
			}

			const result = await authService.logout().catch(() => null)
			if (result?.logoutUrl) {
				window.location.href = result.logoutUrl
			} else {
				window.location.reload()
			}
		}
	}
}
</script>

<style scoped lang="scss">
img[alt="Equivate logo"] {
	width: 152px;
	height: 37px;
}

.login-image {
	display: block;
	width: 100%;
	max-width: 461px;
	height: auto;
}

.sso-button {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.75rem;
	padding: 0.75rem 1.5rem;
	border: 1px solid #dee2e6;
	border-radius: 0.5rem;
	background: #fff;
	color: #212529;
	text-decoration: none;
	font-weight: 500;
	transition: background 0.15s ease, border-color 0.15s ease;

	&:hover {
		background: #f8f9fa;
		border-color: #adb5bd;
		color: #212529;
	}
}

.sso-icon {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
}
</style>
